import { amountSizeClass, formatAmount } from "@/lib/format-amount";
import { cn } from "@/lib/utils";
import {
	motion,
	useReducedMotion,
	useSpring,
	useTransform,
} from "framer-motion";
import { useEffect, useMemo } from "react";

interface AnimatedNumberProps {
	value: number;
	decimals?: number;
	prefix?: string;
	suffix?: string;
	className?: string;
	adaptive?: boolean;
	size?: "xs" | "sm" | "md" | "lg" | "xl";
	compact?: boolean;
	usd?: boolean;
}

function formatValue(
	v: number,
	decimals: number,
	prefix: string,
	suffix: string,
	compact: boolean,
	usd: boolean,
): string {
	if (usd) {
		return formatAmount(v, { decimals, usd: true, compact }) + (suffix || "");
	}
	return `${prefix}${formatAmount(v, { decimals, compact })}${suffix}`;
}

export function AnimatedNumber({
	value,
	decimals = 2,
	prefix = "",
	suffix = "",
	className,
	adaptive = true,
	size = "md",
	compact = false,
	usd = false,
}: AnimatedNumberProps) {
	const reducedMotion = useReducedMotion();
	const spring = useSpring(value, { stiffness: 120, damping: 20 });
	const display = useTransform(spring, (v) =>
		formatValue(v, decimals, prefix, suffix, compact, usd),
	);
	const staticText = useMemo(
		() => formatValue(value, decimals, prefix, suffix, compact, usd),
		[value, decimals, prefix, suffix, compact, usd],
	);
	const sizeClass = adaptive ? amountSizeClass(staticText, size) : undefined;

	useEffect(() => {
		if (reducedMotion) spring.jump(value);
		else spring.set(value);
	}, [value, spring, reducedMotion]);

	if (reducedMotion) {
		return (
			<span
				className={cn("tabular-nums min-w-0", sizeClass, className)}
				aria-live="polite"
			>
				{staticText}
			</span>
		);
	}

	return (
		<motion.span
			className={cn("tabular-nums min-w-0", sizeClass, className)}
			aria-live="polite"
		>
			{display}
		</motion.span>
	);
}
